/* eslint-disable react/jsx-props-no-spreading */
import classnames from 'classnames';
import { type FC, useId } from 'react';
import * as React from 'react';

import type {
    ProductNutrientInformation,
    ProductNutritionFact,
} from '../../../../../../common/models/props/itemTeaserProps';
import Disclaimer from '../Disclaimer';
import ProductDetailHeading from '../ProductDetailHeading';
import styles from './ProductNutritionTable.module.scss';
import {
    getNutrientBasis,
    getSchemaPropName,
    getUnitShortFromLongValue,
} from './ProductNutritionTable.utils';

interface ProductNutritionTableProps {
    nutrientInformation?: ProductNutrientInformation[];
    nutritionFacts: ProductNutritionFact[];
}

const ProductNutritionTable: FC<React.PropsWithChildren<ProductNutritionTableProps>> = (props) => {
    const hasDailyIntakeInfo = props.nutritionFacts.some((fact) => !!fact.percentageOfDailyIntake);
    const nutritionFacts = props.nutritionFacts
        .filter((x) => !!x && x.name && x.value)
        .map((x) => ({ ...x, value: x.value.replace('.', ',') })); // later on nutritionFacts will be inside nutrientInformation as details so this field should be removed
    const nutrientInformation = props.nutrientInformation?.[0].header; // this should be a map on array but they dont have all the info yet in AS
    const nutrientBasis = getNutrientBasis(nutrientInformation);

    const dailyIntakeHtmlId = useId();

    return (
        <div className={styles.Container}>
            <ProductDetailHeading>
                Näringsvärde per {nutrientBasis}
                {nutrientInformation?.preparationState?.code === 'PREPARED' &&
                    ` (${nutrientInformation?.preparationState?.value})`}
            </ProductDetailHeading>
            <div className={styles.NutritionGrid}>
                <div className={classnames('u-textWeightBold', styles.NutritionName, styles.Cell)}>
                    Innehåll
                </div>
                <div className={classnames('u-textWeightBold', styles.NutritionValue, styles.Cell)}>
                    {nutrientBasis}
                </div>
                {hasDailyIntakeInfo && (
                    <div
                        className={classnames(
                            'u-textWeightBold',
                            styles.NutritionDailyIntake,
                            styles.Cell,
                        )}
                        aria-labelledby={dailyIntakeHtmlId}
                    >
                        RI*
                    </div>
                )}
                <div className={styles.GridLine} />
                {nutritionFacts.map((x, index) => (
                    <React.Fragment key={`${x.name}_${x.value}_${x.unit}_${index.toString()}`}>
                        <div
                            itemProp={getSchemaPropName(x.name) || undefined}
                            className={classnames(styles.NutritionName, styles.Cell)}
                        >
                            {x.name}
                        </div>
                        <div className={classnames(styles.NutritionValue, styles.Cell)}>{`${
                            x.value
                        } ${getUnitShortFromLongValue(x.unit)}`}</div>
                        {hasDailyIntakeInfo && (
                            <div className={classnames(styles.NutritionDailyIntake, styles.Cell)}>
                                {x.percentageOfDailyIntake && `${x.percentageOfDailyIntake}%`}
                            </div>
                        )}
                        <div className={styles.GridLine} />
                    </React.Fragment>
                ))}
            </div>
            {hasDailyIntakeInfo && (
                <span className="u-textXSmall" id={dailyIntakeHtmlId}>
                    <span aria-hidden>*</span>Referensintag
                </span>
            )}
            <div className="u-marginTmd">
                <Disclaimer />
            </div>
        </div>
    );
};

export default ProductNutritionTable;
